import React, { useCallback, useEffect, useMemo } from 'react';
import EquipmentScope from './EquipmentScope';
import { Box, Button, Card, CardContent, CircularProgress, Grid, IconButton, Typography, useMediaQuery } from '@mui/material';
import { useNavigate, useParams } from "react-router";
import axiosBase from '../common/axiosBase';
import coreConfig from '../common/coreConfig';
import HelpIcon from '@mui/icons-material/Help';
import { useTheme } from '@emotion/react';
import { logger } from '../App';

const { apiConfig } = coreConfig;

const colorMapper = {
    Draft: "#efc630",
    "Locked for Approval": "#efc630",
    Production: "#81eb81",
    Archived: "#b5b5b5"
};
const permissions = { add: false, edit: false, delete: false };

function ArchiveScreen() {
    const [state, setState] = React.useState({
        data: {},
        loading: false
    });
    const theme = useTheme();
    const screen = useMediaQuery(theme.breakpoints.between('sm', 'md'));
    const is1024x768 = useMediaQuery('(max-width: 1024px) and (max-height: 768px)');
    let { revisionId } = useParams();
    revisionId = Number(revisionId);
    const navigate = useNavigate();
    const fetchData = async () => {
        try {
            const response = await axiosBase({
                method: apiConfig.reqMethod.GET,
                api: apiConfig.endpoints.revision + "/" + revisionId
            });
            setState((prevState) => ({
                ...prevState,
                data: response.data.data,
                loading: false
            }));
        } catch (error) {
            setState((prevState) => ({
                ...prevState,
                loading: false
            }));
            logger.error({ error, message: "Error fetching revision data" });
        }
    };
    const { data } = state;

    const onCellDoubleClickOverride = useCallback(({ id }) => {
        navigate(`/validatescope/${data.EquipmentId}/${id}`);
    }, [data.EquipmentId, navigate]);

    useEffect(() => {
        if(!revisionId) return;
        fetchData();
    }, [revisionId]);
    
    const equipmentScopeBaseFilters = useMemo(() => [{
        field: "RevisionId",
        operator: "equals",
        value: revisionId,
        type: "number"
    }], [revisionId]);

    if (state.loading || !revisionId) {
        return <CircularProgress style={{ display: "flex", margin: "auto" }} />;
    }

    return (
        <div>
            <Grid size={{ xs:12, md: 12 }} sx={{ marginBottom: '10px', marginTop: "10px" }}>
                <Box display="flex" alignItems="center" justifyContent='space-between' sx={{
                    backgroundColor: colorMapper[data.RevisionStatus] || "#fff",
                    borderRadius: '5px',
                    padding: '10px'
                }}>
                    <Typography variant="h5" sx={{ color: 'text.secondary' }}><b>Archives</b></Typography>
                    <Typography variant="h5" m="auto" sx={{ color: 'text.secondary' }}>{data.RevisionStatus} - {data.Revision}</Typography>
                    <Box>
                        <Button variant="contained" onClick={() => navigate("/dashboard")}>Back</Button>
                        <IconButton color="primary" title='Help' size="large">
                            <HelpIcon fontSize="inherit" />
                        </IconButton>
                    </Box>
                </Box>
            </Grid>
            <Grid container my={3}>
                <Grid size={12} height={'fit-content'}>
                    <Card>
                        <CardContent>
                            <Grid container spacing={2} height={'fit-content'}>
                                <Card sx={{ backgroundColor: '#d3d0d094', padding: '1em', textWrap: 'nowrap', width: '30%', height: '12.5em', alignSelf: 'center', marginLeft: '4em' }}>
                                    <Grid size={{ xs: 6, md: is1024x768 ? 6 : 3 }} display="flex" justifyContent="space-between" height={'fit-content'}>
                                        <Grid
                                            container
                                            sx={{
                                                flexDirection: 'row-reverse',
                                                padding: '1em',
                                                '& .keyValueRow': {
                                                    display: 'grid',
                                                    gridTemplateColumns: screen || is1024x768 ? '130px 50px 50px' : '150px 100px 100px',
                                                    '& .keyLabel': { fontWeight: 'bold', width: 'auto' },
                                                    '& .keyValue': { marginLeft: screen || is1024x768 ? '-2em' : '-4em' }
                                                }
                                            }}
                                        >
                                            <Grid size={{ xs: 12, md: 8 }} className="keyValueRow">
                                                <Typography variant="body1" className="keyLabel">
                                                    Equipment Model :
                                                </Typography>
                                                <Typography variant="body1" component="span">
                                                    {' '}
                                                </Typography>
                                                <Typography variant="body1" className="keyValue">
                                                    {data.ModelName || ""}
                                                </Typography>
                                            </Grid>
                                            <Grid size={{ xs: 12, md: 8 }} className="keyValueRow">
                                                <Typography variant="body1" className="keyLabel">
                                                    Equipment Type :
                                                </Typography>
                                                <Typography variant="body1" component="span">
                                                    {' '}
                                                </Typography>
                                                <Typography variant="body1" className="keyValue">
                                                    {' '}
                                                    {data.EquipmentType || ""}
                                                </Typography>
                                            </Grid>
                                            <Grid size={{ xs:12, md: 8 }} className="keyValueRow">
                                                <Typography variant="body1" className="keyLabel">
                                                    Equipment Family :
                                                </Typography>
                                                <Typography variant="body1" component="span">
                                                    {' '}
                                                </Typography>
                                                <Typography variant="body1" className="keyValue">
                                                    {' '}
                                                    {data.EquipmentFamily || ""}
                                                </Typography>
                                            </Grid>
                                            <Grid size={{ xs: 12, md: 8 }} className="keyValueRow">
                                                <Typography variant="body1" className="keyValue">
                                                    {' '}
                                                    <br></br>
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Card>
                                <Grid size={{ xs: 6, md: is1024x768 ? 6 : 2 }} display="flex" justifyContent="space-between">
                                    <Box>
                                        <Box
                                            component="img"
                                            src={`/${data.EquipmentTypeImage}`}
                                            alt="washer"
                                            sx={{ height: 245, paddingLeft: 10, marginBottom: '1em' }}
                                        />
                                    </Box>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
            <EquipmentScope.Grid
                baseFilters={equipmentScopeBaseFilters}
                permissions={permissions}
                onCellDoubleClickOverride={onCellDoubleClickOverride}
            />
        </div>
    );
}

export default ArchiveScreen;